import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ControlledSelect } from '#components/form/FormSelect.tsx';
import type { OrderFormData } from '#components/order/CreateOrderDialog/OrderFormValidation.ts';
import type { BulkMaterialType } from '#domain/BulkMaterial.ts';
import type { CargoMaterialType } from '#domain/CargoMaterial.ts';
import type { MaterialType } from '#domain/Material.ts';
import type { ImportOrderFormData } from '#features/csvImport/ImportOrderFormValidation.ts';

import { ControlledFormInput, ControlledNumberFormInput } from '../../form/FormInput.tsx';
import { Between } from './Between.tsx';
import { MaterialPackageTypeSelect } from './MaterialPackageTypeSelect.tsx';
import { unitOptions } from './unitOptions.ts';

type LoadFormProps = {
    index: number;
    materialPrefix: `orders.${number}.materials.${number}` | `materials.${number}`;
    materialType: CargoMaterialType | BulkMaterialType;
    onPackageTypeSelect?: (packageType: string, type: MaterialType) => boolean;
};

export const LoadForm = ({ materialPrefix, materialType, index, onPackageTypeSelect }: LoadFormProps) => {
    const intl = useIntl();
    const { control, getValues, setValue } = useFormContext<OrderFormData | ImportOrderFormData>();
    const unitTypeFieldName = `${materialPrefix}.unitType` as const;

    useEffect(() => {
        if (materialType !== 'bulk-material') {
            return;
        }
        if (getValues(unitTypeFieldName)) {
            return;
        }
        setValue(unitTypeFieldName, 'ton');
    }, [getValues, materialType, setValue, unitTypeFieldName]);

    return (
        <div className={'display-flex align-items-center gap-10'}>
            <MaterialPackageTypeSelect
                materialPrefix={materialPrefix}
                index={index}
                onPackageTypeSelect={onPackageTypeSelect}
            />
            <Between>x</Between>
            <ControlledNumberFormInput
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.freight.quantity',

                    defaultMessage: 'Quantity',
                })}
                formGroupClassName={'width-60'}
                className='width-60'
                name={`${materialPrefix}.quantity`}
                numericMode={materialType === 'cargo-material' ? 'integer' : 'decimal'}
                control={control}
            />

            {materialType === 'bulk-material' && (
                <ControlledSelect
                    control={control}
                    formGroupClassName={'flex-0-1'}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.common.unit',

                        defaultMessage: 'Unit',
                    })}
                    name={`${materialPrefix}.unitType`}
                    options={unitOptions}
                />
            )}

            <Between>
                {intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.freight.load-preposition',

                    defaultMessage: 'containing',
                })}
            </Between>
            <ControlledFormInput
                formGroupClassName={'flex-1-1-0'}
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.freight.load',

                    defaultMessage: 'Load',
                })}
                name={`${materialPrefix}.name`}
                control={control}
            />
        </div>
    );
};
